import * as Sentry from '@sentry/react';

export const initSentry = () => {
  const isProduction = import.meta.env.MODE === 'production';
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (isProduction && sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [
        // BrowserTracing integration would be added here in production
      ],
      // Performance Monitoring
      tracesSampleRate: isProduction ? 0.1 : 1.0,
      // Environment
      environment: import.meta.env.MODE || 'development',
      // Release version
      release: import.meta.env.VITE_APP_VERSION || '1.0.0',
      // User context
      beforeSend(event, hint) {
        // Filter out non-critical errors in production
        if (isProduction) {
          const error = hint.originalException;

          // Don't send network errors that are likely user connectivity issues
          if (error instanceof Error) {
            if (
              error.message.includes('fetch') ||
              error.message.includes('NetworkError') ||
              error.message.includes('offline')
            ) {
              return null;
            }
          }

          // Don't send cancelled requests
          if (error instanceof DOMException && error.name === 'AbortError') {
            return null;
          }
        }

        return event;
      },
      // Add user context automatically
      initialScope: {
        tags: {
          component: 'frontend',
        },
      },
    });

    // Set up global error handlers
    window.addEventListener('unhandledrejection', event => {
      console.error('Unhandled promise rejection:', event.reason);
      Sentry.captureException(event.reason);
    });

    // Performance monitoring
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            if (navEntry.loadEventEnd - navEntry.loadEventStart > 3000) {
              Sentry.addBreadcrumb({
                message: 'Slow page load detected',
                level: 'warning',
                data: {
                  loadTime: navEntry.loadEventEnd - navEntry.loadEventStart,
                  url: window.location.href,
                },
              });
            }
          }
        }
      });

      observer.observe({ entryTypes: ['navigation'] });
    }
  } else {
    // Development mode - still capture errors but log them
    console.log('Sentry disabled in development mode');
  }
};

export const captureError = (error: Error, context?: Record<string, any>) => {
  if (import.meta.env.MODE === 'production') {
    Sentry.withScope(scope => {
      if (context) {
        Object.keys(context).forEach(key => {
          scope.setContext(key, context[key]);
        });
      }
      Sentry.captureException(error);
    });
  } else {
    console.error('Error captured:', error, context);
  }
};

export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, any>
) => {
  if (import.meta.env.MODE === 'production') {
    Sentry.withScope(scope => {
      if (context) {
        Object.keys(context).forEach(key => {
          scope.setContext(key, context[key]);
        });
      }
      scope.setLevel(level);
      Sentry.captureMessage(message);
    });
  } else {
    console.log(`[${level.toUpperCase()}] ${message}`, context);
  }
};

export const setUserContext = (user: {
  id: string;
  email: string;
  [key: string]: any;
}) => {
  if (import.meta.env.MODE === 'production') {
    Sentry.setUser(user);
  }
};

export const clearUserContext = () => {
  if (import.meta.env.MODE === 'production') {
    Sentry.setUser(null);
  }
};

// Performance monitoring helpers
export const measurePerformance = (name: string, fn: () => void) => {
  const start = performance.now();
  try {
    fn();
  } finally {
    const duration = performance.now() - start;
    if (duration > 100) {
      // Log operations taking more than 100ms
      captureMessage(`Slow operation: ${name}`, 'warning', { duration });
    }
  }
};

export const measureAsyncPerformance = async <T>(
  name: string,
  fn: () => Promise<T>
): Promise<T> => {
  const start = performance.now();
  try {
    return await fn();
  } finally {
    const duration = performance.now() - start;
    if (duration > 1000) {
      // Log async operations taking more than 1s
      captureMessage(`Slow async operation: ${name}`, 'warning', { duration });
    }
  }
};

// Error boundary integration - simplified for testing compatibility
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>
) => {
  if (import.meta.env.MODE === 'production') {
    return Sentry.withErrorBoundary(Component, {
      beforeCapture: scope => {
        scope.setTag('errorBoundary', true);
      },
    });
  }
  return Component;
};

// Network error tracking
export const trackNetworkError = (
  url: string,
  method: string,
  status?: number,
  error?: Error
) => {
  Sentry.addBreadcrumb({
    message: `Network error: ${method} ${url}`,
    level: 'error',
    data: {
      url,
      method,
      status,
      error: error?.message,
    },
  });

  if (error) {
    Sentry.withScope(scope => {
      scope.setTag('errorType', 'network');
      scope.setContext('networkRequest', { url, method, status });
      Sentry.captureException(error);
    });
  }
};

// User interaction tracking
export const trackUserAction = (action: string, data?: Record<string, any>) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    level: 'info',
    data,
  });
};

export { Sentry };
